import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Avatar, Box, Skeleton, Text, useTheme } from '@ublend-npm/aulaui-core';
import { currentUser } from '@core/selectors/user';
import { getHomeUserFirstName } from '@app/selectors/home';
import GreetingTimeIcon, {
  getGreetingTimeOfDay,
  GREETING_TIME_LABELS,
  GreetingTimeOfDay,
} from './GreetingTimeIcon';
import { HOME_WIDGET_FONT_FAMILY } from '../homeWidgetTypography';
import {
  GREETING_CARD_HEIGHT,
  GREETING_CARD_WIDTH,
} from '../spaceWidget/spaceWidgetConstants';

const GREETING_SUBTITLE_COLOR = 'rgba(255, 255, 255, 0.7)';
/** Figma var(--AulaPurple-600). */
const GREETING_CARD_BG = 'rgba(81, 48, 147, 1)';
const GREETING_CARD_SHADOW = '0 0.5rem 1rem 0 rgba(0, 0, 0, 0.08)';
/** Figma var(--_components-avatar-fill). */
const AVATAR_BG = 'rgba(215, 210, 223, 1)';
/** Figma var(--background-paper-elevation-0). */
const AVATAR_TEXT_COLOR = 'rgba(18, 18, 18, 1)';
const SKELETON_SURFACE = 'rgba(255, 255, 255, 0.22)';

/** Figma Greetings Widget — padding & sizing (with space cards). */
const LG_PADDING_Y = '1.5rem'; // 24px
const LG_PADDING_X = '1rem'; // 16px
/** Inner frame inside card padding (232 − 16×2) × (272 − 24×2). */
const LG_INNER_WIDTH = '12.5rem'; // 200px
const LG_INNER_HEIGHT = '14rem'; // 224px
const LG_INNER_GAP = '0.5rem'; // 8px
/** Offset from inner top to content container top. */
const LG_CONTENT_OFFSET_TOP = '3.9375rem'; // 63px
const LG_CONTENT_HEIGHT = '10.0625rem'; // 161px
const LG_CONTENT_GAP = '0.5rem'; // 8px
const LG_AVATAR_SIZE = '2.5rem'; // 40px
const LG_GREETING_BLOCK_HEIGHT = '4rem'; // 64px
const LG_DATE_WIDTH = '11rem'; // 176px
const LG_ICON_SIZE = '4.6875rem'; // 75px

const cardShellSx = {
  position: 'relative' as const,
  borderRadius: '1.5rem', // 24px
  boxShadow: GREETING_CARD_SHADOW,
  boxSizing: 'border-box' as const,
  width: {
    xs: '100%',
    sm: '100%',
    md: '100%',
    lg: '100%',
  },
  minWidth: {
    lg: GREETING_CARD_WIDTH,
  },
  height: {
    xs: '5.5rem', // 88px
    sm: '5.5rem',
    md: '4.375rem', // 70px
    lg: '100%',
  },
  minHeight: {
    lg: GREETING_CARD_HEIGHT,
  },
  flexShrink: 0,
  alignSelf: 'stretch',
  px: {
    xs: 2,
    sm: 2,
    md: 3,
    lg: LG_PADDING_X,
  },
  py: {
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    lg: LG_PADDING_Y,
  },
};

const mobileIconSx = {
  position: 'absolute' as const,
  top: { xs: 12, sm: 12, md: 10 },
  right: { xs: 16, sm: 16, md: 24 },
  width: { xs: 40, sm: 40, md: 48 },
  height: { xs: 40, sm: 40, md: 48 },
  '& svg': { width: '100%', height: '100%' },
};

const lgIconSx = {
  position: 'absolute' as const,
  top: LG_PADDING_Y,
  right: LG_PADDING_X,
  width: LG_ICON_SIZE,
  height: LG_ICON_SIZE,
  '& svg': { width: '100%', height: '100%' },
};

const lgGreetingTextSx = {
  fontFamily: HOME_WIDGET_FONT_FAMILY,
  fontSize: '1.5rem',
  fontWeight: 500,
  lineHeight: '133%',
  letterSpacing: 0,
};

const lgDateTextSx = {
  fontFamily: HOME_WIDGET_FONT_FAMILY,
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: GREETING_SUBTITLE_COLOR,
  width: LG_DATE_WIDTH,
};

const getAvatarAltText = (
  user?: { firstName?: string; lastName?: string } | null
) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name ? `Profile picture of ${name}` : 'Profile picture';
};

const greetingFocusSx = {
  outline: 'none',
  '&:focus-visible': {
    outline: '2px solid #FFFFFF',
    outlineOffset: '2px',
  },
};

/** Compact (MD-style) shell — short horizontal bar; used when greeting sits above the widget. */
const compactCardShellSx = {
  ...cardShellSx,
  width: '100%',
  minWidth: undefined,
  height: { xs: '88px', sm: '88px', md: '70px', lg: '70px' },
  minHeight: undefined,
  px: { xs: 2, sm: 2, md: 3, lg: 3 },
  py: { xs: 1.5, sm: 1.5, md: 1.5, lg: 1.5 },
};

type HomeGreetingSkeletonProps = {
  backgroundColor: string;
  isLg?: boolean;
  compact?: boolean;
};

const HomeGreetingSkeleton = ({
  backgroundColor,
  isLg = false,
  compact = false,
}: HomeGreetingSkeletonProps) => (
  <Box
    component="section"
    tabIndex={0}
    role="status"
    aria-live="polite"
    aria-busy="true"
    aria-label="Loading greeting"
    sx={{
      ...(compact ? compactCardShellSx : cardShellSx),
      ...greetingFocusSx,
      backgroundColor,
      overflow: 'hidden',
      display: isLg ? 'flex' : 'block',
      flexDirection: isLg ? 'column' : undefined,
    }}
  >
    <Skeleton
      kind="circular"
      sx={{
        ...(isLg ? lgIconSx : mobileIconSx),
        borderRadius: '50%',
        bgcolor: SKELETON_SURFACE,
      }}
    />

    {isLg ? (
      <Box
        sx={{
          width: LG_INNER_WIDTH,
          height: LG_INNER_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          gap: LG_INNER_GAP,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            mt: LG_CONTENT_OFFSET_TOP,
            width: LG_INNER_WIDTH,
            height: LG_CONTENT_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            gap: LG_CONTENT_GAP,
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <Skeleton
            kind="circular"
            width={40}
            height={40}
            sx={{
              bgcolor: SKELETON_SURFACE,
              flexShrink: 0,
            }}
          />
          <Skeleton
            kind="rounded"
            height={32}
            sx={{
              width: '100%',
              borderRadius: '0.375rem',
              bgcolor: SKELETON_SURFACE,
              flexShrink: 0,
            }}
          />
          <Skeleton
            kind="rounded"
            height={32}
            sx={{
              width: '72%',
              borderRadius: '0.375rem',
              bgcolor: SKELETON_SURFACE,
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Skeleton
            kind="rounded"
            height={24}
            sx={{
              width: LG_DATE_WIDTH,
              borderRadius: '0.375rem',
              bgcolor: SKELETON_SURFACE,
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>
    ) : (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          gap: 2,
          pr: '56px',
          overflow: 'hidden',
        }}
      >
        <Skeleton
          kind="circular"
          width={40}
          height={40}
          sx={{ bgcolor: SKELETON_SURFACE, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            kind="rounded"
            height={22}
            sx={{
              bgcolor: SKELETON_SURFACE,
              width: '92%',
              borderRadius: '6px',
            }}
          />
          <Skeleton
            kind="rounded"
            height={18}
            sx={{
              bgcolor: SKELETON_SURFACE,
              width: '68%',
              borderRadius: '6px',
              mt: '6px',
            }}
          />
        </Box>
      </Box>
    )}
  </Box>
);

type HomeGreetingProps = {
  /** Forces the MD-style short horizontal greeting regardless of breakpoint. */
  compact?: boolean;
};

const HomeGreeting = ({ compact = false }: HomeGreetingProps) => {
  const theme = useTheme();
  const { palette } = theme;
  const user = useSelector(currentUser);
  const firstName = useSelector(getHomeUserFirstName);
  const [timeOfDay, setTimeOfDay] = useState<GreetingTimeOfDay | null>(null);
  const [isLgViewport, setIsLgViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(min-width: ${theme.breakpoints.values.lg}px)`)
      .matches;
  });
  const isLg = compact ? false : isLgViewport;

  const mobileGreetingTextSx = {
    fontFamily: HOME_WIDGET_FONT_FAMILY,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: compact
      ? { xs: '1.125rem', sm: '1.125rem', md: '1.25rem', lg: '1.25rem' }
      : { xs: '1.125rem', sm: '1.125rem', md: '1.25rem', lg: '1.5rem' },
    lineHeight: '133%',
    letterSpacing: compact ? { sm: 0 } : { sm: 0, lg: 0 },
    color: palette.common.white,
  };

  useEffect(() => {
    const mq = window.matchMedia(
      `(min-width: ${theme.breakpoints.values.lg}px)`
    );
    const update = () => setIsLgViewport(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [theme.breakpoints.values.lg]);

  useEffect(() => {
    setTimeOfDay(getGreetingTimeOfDay());
  }, []);

  const todayLabel = moment().format('[Today is] Do MMMM YYYY');
  const initials = [user?.firstName?.[0], user?.lastName?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();
  const avatarAltText = getAvatarAltText(user);

  if (!timeOfDay) {
    return (
      <HomeGreetingSkeleton
        backgroundColor={GREETING_CARD_BG}
        isLg={isLg}
        compact={compact}
      />
    );
  }

  const greetingLabel = GREETING_TIME_LABELS[timeOfDay];

  return (
    <Box
      component="section"
      tabIndex={0}
      aria-labelledby="home-greeting-heading"
      sx={{
        ...(compact ? compactCardShellSx : cardShellSx),
        ...greetingFocusSx,
        backgroundColor: GREETING_CARD_BG,
        color: palette.common.white,
        overflow: compact
          ? 'hidden'
          : { xs: 'hidden', sm: 'hidden', md: 'hidden', lg: 'visible' },
        display: compact ? 'block' : { xs: 'block', lg: 'flex' },
        flexDirection: compact ? undefined : { lg: 'column' },
      }}
    >
      <GreetingTimeIcon
        timeOfDay={timeOfDay}
        sx={{
          position: 'absolute',
          top: compact
            ? { xs: 12, sm: 12, md: 10, lg: 10 }
            : { xs: 12, sm: 12, md: 10, lg: LG_PADDING_Y },
          right: compact
            ? { xs: 16, sm: 16, md: 24, lg: 24 }
            : { xs: 16, sm: 16, md: 24, lg: LG_PADDING_X },
          width: compact
            ? { xs: 40, sm: 40, md: 48, lg: 48 }
            : { xs: 40, sm: 40, md: 48, lg: LG_ICON_SIZE },
          height: compact
            ? { xs: 40, sm: 40, md: 48, lg: 48 }
            : { xs: 40, sm: 40, md: 48, lg: LG_ICON_SIZE },
          '& svg': { width: '100%', height: '100%' },
        }}
      />

      {/* Desktop (LG) — Figma: 200×224 inner, content 161 offset 63 from top, icon 75 */}
      <Box
        aria-hidden={!isLg}
        sx={{
          display: compact
            ? 'none'
            : { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
          flexDirection: 'column',
          width: LG_INNER_WIDTH,
          height: LG_INNER_HEIGHT,
          gap: LG_INNER_GAP,
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            mt: LG_CONTENT_OFFSET_TOP,
            width: LG_INNER_WIDTH,
            height: LG_CONTENT_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            gap: LG_CONTENT_GAP,
            boxSizing: 'border-box',
            flexShrink: 0,
            minHeight: 0,
          }}
        >
          <Avatar
            src={user?.avatar}
            altText={avatarAltText}
            fallbackText={initials || '?'}
            variant="circular"
            tabIndex={0}
            sx={{
              width: LG_AVATAR_SIZE,
              height: LG_AVATAR_SIZE,
              flexShrink: 0,
              backgroundColor: AVATAR_BG,
              color: AVATAR_TEXT_COLOR,
              fontSize: '1.25rem',
              fontWeight: 400,
              lineHeight: '1.25rem',
              letterSpacing: '0.14px',
            }}
          />
          <Text
            component="h2"
            tabIndex={0}
            id={isLg ? 'home-greeting-heading' : undefined}
            sx={{
              m: 0,
              p: 0,
              height: LG_GREETING_BLOCK_HEIGHT,
              maxWidth: '100%',
              flexShrink: 0,
              overflow: 'hidden',
              fontWeight: lgGreetingTextSx.fontWeight,
              fontSize: lgGreetingTextSx.fontSize,
              lineHeight: lgGreetingTextSx.lineHeight,
              letterSpacing: lgGreetingTextSx.letterSpacing,
              fontFamily: lgGreetingTextSx.fontFamily,
              color: palette.common.white,
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {greetingLabel}{' '}
              <Box component="span" role="img" aria-label="waving hand">
                👋
              </Box>
            </Box>
            <Box
              component="span"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {firstName}
            </Box>
          </Text>

          <Box sx={{ flex: 1, minHeight: 0 }} />

          <Text
            component="time"
            sx={{
              m: 0,
              p: 0,
              flexShrink: 0,
              display: 'block',
              ...lgDateTextSx,
            }}
            tabIndex={0}
          >
            {todayLabel}
          </Text>
        </Box>
      </Box>

      {/* Mobile + Tablet */}
      <Box
        aria-hidden={isLg}
        sx={{
          display: compact
            ? 'flex'
            : { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' },
          alignItems: 'center',
          height: '100%',
          gap: 2,
          pr: '56px',
          overflow: 'hidden',
          borderRadius: '8px',
        }}
        tabIndex={0}
      >
        <Avatar
          src={user?.avatar}
          altText={avatarAltText}
          fallbackText={initials || '?'}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            backgroundColor: AVATAR_BG,
            color: AVATAR_TEXT_COLOR,
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
          tabIndex={0}
        />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Text
            component="h2"
            id={isLg ? undefined : 'home-greeting-heading'}
            sx={{
              m: 0,
              fontWeight: mobileGreetingTextSx.fontWeight,
              fontSize: mobileGreetingTextSx.fontSize,
              lineHeight: mobileGreetingTextSx.lineHeight,
              letterSpacing: mobileGreetingTextSx.letterSpacing,
              fontFamily: mobileGreetingTextSx.fontFamily,
              color: mobileGreetingTextSx.color,
            }}
            tabIndex={0}
          >
            {greetingLabel}
            <Box
              component="span"
              role="img"
              aria-label="waving hand"
              tabIndex={0}
            >
              {' '}
              👋
            </Box>
            , {firstName}
          </Text>

          <Text
            component="time"
            sx={{
              m: 0,
              mt: '2px',
              fontFamily: HOME_WIDGET_FONT_FAMILY,
              fontSize: '0.875rem',
              fontWeight: 400,
              lineHeight: '21px',
              color: GREETING_SUBTITLE_COLOR,
              letterSpacing: { lg: 0 },
              display: 'block',
            }}
            tabIndex={0}
          >
            {todayLabel}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default HomeGreeting;
